import { useState, useEffect } from "react";
import client from "@/api";
import { useApp } from "@/context/AppContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { PasswordInput } from "@/components/PasswordInput";
import { KeyRound, CheckCircle2 } from "lucide-react";

function fmtErr(detail, fallback) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
}

export default function ResetPassword() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setToken(params.get("token") || "");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error(t("reset_invalid")); return; }
    if (password !== confirm) { toast.error(t("pw_mismatch")); return; }
    setBusy(true);
    try {
      await client.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success(t("reset_done"));
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail, t("error")));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] bg-grain">
      <Header variant="public" />
      <div className="flex items-center justify-center px-4 py-12">
        <div className="card-dark rounded-2xl p-8 w-full max-w-md fade-up" data-testid="reset-card">
          {done ? (
            <div className="text-center py-4" data-testid="reset-done">
              <CheckCircle2 className="w-12 h-12 text-[#D21F2A] mx-auto mb-4" />
              <h1 className="font-serif text-xl text-white mb-2">{t("reset_done")}</h1>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-form">
              <div className="flex items-center gap-3 mb-6">
                <KeyRound className="w-7 h-7 text-[#D21F2A]" />
                <h1 className="font-serif text-2xl text-white">{t("reset_title")}</h1>
              </div>
              <label className="block text-xs uppercase tracking-wider text-[#C9CDD3] mb-1">{t("password")}</label>
              <div className="mb-1"><PasswordInput testid="reset-password-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" /></div>
              <p className="text-xs text-[#9AA0A6] mb-4">{t("min_pw_hint")}</p>
              <label className="block text-xs uppercase tracking-wider text-[#C9CDD3] mb-1">{t("pw_confirm")}</label>
              <div className="mb-6"><PasswordInput testid="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" /></div>
              <button data-testid="reset-submit" disabled={busy} type="submit"
                className="btn-gold focusable w-full py-3 rounded-lg">{busy ? t("loading") : t("reset_submit")}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
